/**
 * Notification Context
 * Backend API + Socket.IO real-time updates with polling fallback
 * Matching mobile's implementation
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { apiClient } from '@/shared/services/api';
import { API_ROUTES } from '@/shared/services/apiRoutes';
import { API_FEATURES } from '@/shared/services/config';
import socketService from '@/shared/services/socket';

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  action_url: string | null;
  action_screen: string | null;
  action_params: any;
  read: boolean;
  created_at: string;
  read_at: string | null;
}

interface NotificationContextType {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  unreadCount: number;
  addNotification: (notification: Partial<Notification>) => void;
  markAsRead: (id: string) => Promise<boolean>;
  markAllAsRead: () => Promise<boolean>;
  removeNotification: (id: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Polling fallback
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);
  const socketConnected = useRef(false);

  // Local persistence helpers
  const LS_KEY = 'wlkthru.notifications.v2';
  const saveLocal = (items: Notification[]) => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(items));
    } catch (err) {
      console.error('[Notifications] Error saving to localStorage:', err);
    }
  };

  const loadLocal = (): Notification[] => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return [];
      return JSON.parse(raw);
    } catch {
      return [];
    }
  };

  /**
   * Fetch notifications from backend or Supabase
   * Only fetches UNREAD notifications and those created within last 24 hours
   */
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    try {
      setError(null);

      // SECURITY: Use backend API for all notifications
      console.log('[Notifications] Fetching from backend API');

      const response = await apiClient.get<{
        ok: boolean;
        notifications: Notification[];
        unreadCount: number;
      }>(API_ROUTES.NOTIFICATIONS);

      if (!response.ok) throw new Error('Failed to fetch notifications');

      // Filter: only unread OR created within last 24 hours
      const filtered = (response.notifications || []).filter(n => {
        const createdAt = new Date(n.created_at);
        const hoursOld = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);
        return !n.read || hoursOld < 24;
      });

      setNotifications(filtered);
      saveLocal(filtered);
    } catch (err: any) {
      console.error('[Notifications] Error:', err);
      setError(err.message || 'Failed to load notifications');

      // Fallback to localStorage on error
      const local = loadLocal();
      setNotifications(local);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  /**
   * Set up Socket.IO real-time updates
   */
  useEffect(() => {
    if (!user?.id || !API_FEATURES.USE_BACKEND_NOTIFICATIONS) return;

    console.log('[Notifications] Setting up Socket.IO');

    socketService.connect();

    const handleNew = (notification: Notification) => {
      console.log('[Notifications] Socket.IO new:', notification);
      setNotifications((prev) => {
        if (prev.some(n => n.id === notification.id)) return prev;
        const updated = [notification, ...prev];
        saveLocal(updated);
        return updated;
      });
    };

    const handleUpdated = (data: any) => {
      console.log('[Notifications] Socket.IO updated:', data);
      setNotifications((prev) => {
        const updated = prev.map((n) => n.id === data.id ? { ...n, ...data } : n);
        saveLocal(updated);
        return updated;
      });
    };

    const handleDeleted = (data: any) => {
      console.log('[Notifications] Socket.IO deleted:', data);
      setNotifications((prev) => {
        const updated = prev.filter((n) => n.id !== data.id);
        saveLocal(updated);
        return updated;
      });
    };

    const handleAllRead = () => {
      console.log('[Notifications] Socket.IO all read');
      setNotifications((prev) => {
        const updated = prev.map((n) => ({
          ...n,
          read: true,
          read_at: n.read ? n.read_at : new Date().toISOString()
        }));
        saveLocal(updated);
        return updated;
      });
    };

    socketService.on('notification:new', handleNew);
    socketService.on('notification:updated', handleUpdated);
    socketService.on('notification:deleted', handleDeleted);
    socketService.on('notification:all_read', handleAllRead);

    socketConnected.current = socketService.isConnected();

    return () => {
      socketService.off('notification:new', handleNew);
      socketService.off('notification:updated', handleUpdated);
      socketService.off('notification:deleted', handleDeleted);
      socketService.off('notification:all_read', handleAllRead);
    };
  }, [user?.id]);

  // REMOVED: Supabase Realtime - using Socket.IO instead

  /**
   * Polling fallback if WebSocket fails
   */
  useEffect(() => {
    if (!user?.id || !API_FEATURES.USE_BACKEND_NOTIFICATIONS) return;

    // Check if Socket.IO is connected after 5 seconds
    const connectionTimeout = setTimeout(() => {
      if (!socketService.isConnected()) {
        console.log('[Notifications] WebSocket not connected, falling back to polling');

        // Poll every 30 seconds
        pollingInterval.current = setInterval(() => {
          fetchNotifications();
        }, 30000);
      }
    }, 5000);

    // Listen for socket connection and stop polling
    const handleSocketConnect = () => {
      console.log('[Notifications] Socket connected, stopping polling fallback');
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    };

    socketService.on('connect', handleSocketConnect);

    return () => {
      clearTimeout(connectionTimeout);
      socketService.off('connect', handleSocketConnect);
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [user?.id, fetchNotifications]);

  /**
   * Initial fetch
   */
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  /**
   * Refresh when user returns to tab
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Notifications] Tab visible, refreshing...');
        fetchNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchNotifications]);

  // REMOVED: Cleanup is handled by backend

  /**
   * Add notification (local only for now)
   */
  const addNotification = useCallback((notification: Partial<Notification>) => {
    const newNotification: Notification = {
      id: `local_${Date.now()}`,
      user_id: user?.id || '',
      type: notification.type || 'general',
      title: notification.title || 'Notification',
      message: notification.message || '',
      action_url: notification.action_url || null,
      action_screen: notification.action_screen || null,
      action_params: notification.action_params || null,
      read: false,
      created_at: new Date().toISOString(),
      read_at: null,
    };

    setNotifications(prev => {
      const updated = [newNotification, ...prev];
      saveLocal(updated);
      return updated;
    });
  }, [user?.id]);

  /**
   * Mark as read
   */
  const markAsRead = useCallback(async (id: string): Promise<boolean> => {
    if (!user?.id) return false;

    try {
      // SECURITY: Use backend API
      const response = await apiClient.patch<{ ok: boolean }>(
        API_ROUTES.NOTIFICATIONS_MARK_READ(id),
        {}
      );

      if (!response.ok) throw new Error('Failed to mark as read');

      setNotifications(prev => {
        const updated = prev.map(n =>
          n.id === id ? { ...n, read: true, read_at: new Date().toISOString() } : n
        );
        saveLocal(updated);
        return updated;
      });

      return true;
    } catch (err: any) {
      console.error('[Notifications] Error marking as read:', err);
      return false;
    }
  }, [user?.id]);

  /**
   * Mark all as read
   */
  const markAllAsRead = useCallback(async (): Promise<boolean> => {
    if (!user?.id) return false;

    try {
      // SECURITY: Use backend API
      const response = await apiClient.patch<{ ok: boolean }>(
        API_ROUTES.NOTIFICATIONS_READ_ALL,
        {}
      );

      if (!response.ok) throw new Error('Failed to mark all as read');

      setNotifications(prev => {
        const updated = prev.map(n => ({
          ...n,
          read: true,
          read_at: n.read ? n.read_at : new Date().toISOString()
        }));
        saveLocal(updated);
        return updated;
      });

      return true;
    } catch (err: any) {
      console.error('[Notifications] Error marking all as read:', err);
      return false;
    }
  }, [user?.id]);

  /**
   * Remove notification
   */
  const removeNotification = useCallback(async (id: string): Promise<boolean> => {
    if (!user?.id) return false;

    try {
      // SECURITY: Use backend API
      const response = await apiClient.delete<{ ok: boolean }>(
        API_ROUTES.NOTIFICATIONS_DELETE(id)
      );

      if (!response.ok) throw new Error('Failed to delete notification');

      setNotifications(prev => {
        const updated = prev.filter(n => n.id !== id);
        saveLocal(updated);
        return updated;
      });

      return true;
    } catch (err: any) {
      console.error('[Notifications] Error deleting notification:', err);
      return false;
    }
  }, [user?.id]);

  /**
   * Refresh notifications
   */
  const refresh = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  /**
   * Calculate unread count
   */
  const unreadCount = notifications.filter(n => !n.read).length;

  const value: NotificationContextType = {
    notifications,
    loading,
    error,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    refresh,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
